import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Phone, Calendar, Users, Clock, MessageSquare, Loader2, RefreshCw, CheckCircle, AlertCircle, LogOut, ChevronDown, Tag, MapPin, CreditCard, ShieldCheck, XCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [contactRequests, setContactRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [activeTab, setActiveTab] = useState('quotes');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [contactRes, bookingsRes, enquiriesRes] = await Promise.all([
        fetch(`${API_URL}/api/contact-requests`, { credentials: 'include' }),
        fetch(`${API_URL}/api/bookings`, { credentials: 'include' }),
        fetch(`${API_URL}/api/booking-enquiries`, { credentials: 'include' }),
      ]);
      if (!contactRes.ok) throw new Error('Failed to fetch data');
      const contactData = await contactRes.json();
      contactData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setContactRequests(contactData);

      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData);
      }

      if (enquiriesRes.ok) {
        setEnquiries(await enquiriesRes.json());
      }

      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      return;
    }

    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
        navigate('/admin/login', { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
    navigate('/admin/login', { replace: true });
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdatingStatus(requestId);
    try {
      const response = await fetch(`${API_URL}/api/contact-requests/${requestId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      setContactRequests(prev => 
        prev.map(req => req.id === requestId ? { ...req, status: newStatus } : req)
      );
    } catch (err) {
      // Silent fail - UI already reflects current state
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      quoted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || styles.new;
  };

  const statusOptions = ['new', 'contacted', 'quoted', 'completed', 'cancelled'];

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#e2e8f0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 pt-20">
      {/* Header */}
      <section className="bg-slate-900 py-8 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <Badge className="mb-2 bg-[#e2e8f0] text-slate-800 border-0 px-3 py-1 text-xs font-semibold">
                ADMIN DASHBOARD
              </Badge>
              <h1 className="text-3xl font-bold text-white heading-font">
                {activeTab === 'quotes' ? 'Quote Requests' : activeTab === 'bookings' ? 'Bookings' : 'Booking Enquiries'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Last refreshed: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-3 bg-slate-800/50 px-4 py-2 rounded-lg">
                  {user.picture && (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  )}
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                </div>
              )}
              <Button 
                onClick={fetchData} 
                disabled={loading}
                variant="outline"
                className="border-slate-600 text-gray-300 hover:bg-slate-700"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-slate-600 text-gray-300 hover:bg-slate-700"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-slate-800/50 rounded-lg p-1 w-fit" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('quotes')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'quotes'
                ? 'bg-[#e2e8f0] text-slate-900'
                : 'text-gray-400 hover:text-white hover:bg-slate-700'
            }`}
            data-testid="tab-quotes"
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Quotes ({contactRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'bookings'
                ? 'bg-[#e2e8f0] text-slate-900'
                : 'text-gray-400 hover:text-white hover:bg-slate-700'
            }`}
            data-testid="tab-bookings"
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('enquiries')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'enquiries'
                ? 'bg-[#e2e8f0] text-slate-900'
                : 'text-gray-400 hover:text-white hover:bg-slate-700'
            }`}
            data-testid="tab-enquiries"
          >
            <Send className="w-4 h-4 inline mr-2" />
            Enquiries ({enquiries.length})
          </button>
        </div>

        {activeTab === 'quotes' && (
        <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{contactRequests.length}</p>
                  <p className="text-xs text-gray-400">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {contactRequests.filter(r => r.status === 'new').length}
                  </p>
                  <p className="text-xs text-gray-400">New</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {contactRequests.filter(r => r.status === 'contacted').length}
                  </p>
                  <p className="text-xs text-gray-400">Contacted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {contactRequests.filter(r => r.status === 'quoted').length}
                  </p>
                  <p className="text-xs text-gray-400">Quoted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {contactRequests.filter(r => r.status === 'completed').length}
                  </p>
                  <p className="text-xs text-gray-400">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Requests List */}
        {loading && contactRequests.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#e2e8f0] animate-spin" />
          </div>
        ) : error ? (
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-10 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Error Loading Data</h3>
              <p className="text-gray-400 mb-4">{error}</p>
              <Button onClick={fetchData} className="bg-[#e2e8f0] text-slate-900">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : contactRequests.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-10 text-center">
              <MessageSquare className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">No Quote Requests Yet</h3>
              <p className="text-gray-400">When customers submit quote requests, they&apos;ll appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contactRequests.map((request) => (
              <Card 
                key={request.id} 
                className="bg-slate-800/50 border-slate-700/50 hover:bg-slate-800/70 transition-colors"
                data-testid={`request-card-${request.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Customer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-bold text-white truncate">{request.name}</h3>
                        
                        {/* Status Dropdown */}
                        <div className="relative group">
                          <button
                            className={`${getStatusBadge(request.status)} border text-xs px-2 py-1 rounded-full flex items-center gap-1 hover:opacity-80 transition-opacity`}
                            disabled={updatingStatus === request.id}
                          >
                            {updatingStatus === request.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                {request.status.toUpperCase()}
                                <ChevronDown className="w-3 h-3" />
                              </>
                            )}
                          </button>
                          <div className="absolute left-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                            {statusOptions.map((status) => (
                              <button
                                key={status}
                                onClick={() => updateStatus(request.id, status)}
                                className={`w-full text-left px-3 py-2 text-xs capitalize hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg ${
                                  request.status === status ? 'bg-slate-700' : ''
                                } text-gray-300`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <a href={`mailto:${request.email}`} className="text-blue-400 hover:underline truncate">
                            {request.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <a href={`tel:${request.phone}`} className="text-blue-400 hover:underline">
                            {request.phone}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="flex flex-wrap gap-4 lg:gap-6">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-[#e2e8f0]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Event Date</p>
                          <p className="text-sm font-medium text-white">{request.eventDate}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                          <Users className="w-4 h-4 text-[#e2e8f0]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Capacity</p>
                          <p className="text-sm font-medium text-white">{request.venueCapacity}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4 text-[#e2e8f0]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Duration</p>
                          <p className="text-sm font-medium text-white">{request.rentalDuration}</p>
                        </div>
                      </div>

                      {request.eventType && (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                            <Tag className="w-4 h-4 text-[#e2e8f0]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Event Type</p>
                            <p className="text-sm font-medium text-white">{request.eventType}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {request.venueAccessNotes && (
                      <div className="flex items-center gap-2 mt-3">
                        <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                          <MapPin className="w-4 h-4 text-[#e2e8f0]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Venue Access</p>
                          <p className="text-sm font-medium text-white">{request.venueAccessNotes}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  {request.message && (
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <p className="text-xs text-gray-500 mb-1">Requirements</p>
                      <p className="text-sm text-gray-300 leading-relaxed">{request.message}</p>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Submitted: {formatDate(request.createdAt)}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white text-xs"
                        onClick={() => window.location.href = `mailto:${request.email}?subject=Re: Your Audio Apex Quote Request`}
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Email
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white text-xs"
                        onClick={() => window.location.href = `tel:${request.phone}`}
                      >
                        <Phone className="w-3 h-3 mr-1" />
                        Call
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {activeTab === 'bookings' && (
          <BookingsTab bookings={bookings} loading={loading} formatDate={formatDate} />
        )}

        {activeTab === 'enquiries' && (
          <EnquiriesTab enquiries={enquiries} setEnquiries={setEnquiries} loading={loading} formatDate={formatDate} />
        )}
      </div>
    </div>
  );
};


// ============== Bookings Tab Component ==============
const bookingStatusBadge = {
  confirmed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  pending_payment: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const BookingsTab = ({ bookings, loading, formatDate }) => {
  const confirmed = bookings.filter(b => b.status === 'confirmed');
  const pending = bookings.filter(b => b.status === 'pending_payment');
  const expired = bookings.filter(b => b.status === 'expired');

  const totalRevenue = confirmed.reduce((sum, b) => sum + (b.deposit_pence || 0), 0);

  if (loading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#e2e8f0] animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Booking Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{bookings.length}</p>
                <p className="text-xs text-gray-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{confirmed.length}</p>
                <p className="text-xs text-gray-400">Confirmed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{pending.length}</p>
                <p className="text-xs text-gray-400">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">&pound;{(totalRevenue / 100).toLocaleString()}</p>
                <p className="text-xs text-gray-400">Deposits</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-10 text-center">
            <CreditCard className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">No Bookings Yet</h3>
            <p className="text-gray-400">When customers book packages, they&apos;ll appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => (
            <Card
              key={booking.id || booking.booking_ref}
              className="bg-slate-800/50 border-slate-700/50 hover:bg-slate-800/70 transition-colors"
              data-testid={`booking-card-${booking.booking_ref}`}
            >
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Ref + Status */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div>
                      <p className="text-xs text-gray-500">Ref</p>
                      <p className="text-sm font-mono font-bold text-white tracking-wider">{booking.booking_ref}</p>
                    </div>
                    <Badge className={`${bookingStatusBadge[booking.status] || bookingStatusBadge.expired} border text-[10px] px-2`}>
                      {booking.status === 'pending_payment' ? 'PENDING' : (booking.status || 'unknown').toUpperCase()}
                    </Badge>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-x-6 gap-y-2 flex-1 text-sm">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Package</p>
                      <p className="text-gray-200 font-medium">{booking.package_name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Event Date</p>
                      <p className="text-gray-200">{booking.event_date}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Customer</p>
                      <p className="text-gray-200">{booking.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Email</p>
                      <a href={`mailto:${booking.customer_email}`} className="text-blue-400 hover:underline text-xs">
                        {booking.customer_email}
                      </a>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Deposit</p>
                      <p className="text-emerald-400 font-semibold">&pound;{((booking.deposit_pence || 0) / 100).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white text-xs"
                      onClick={() => window.location.href = `mailto:${booking.customer_email}`}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                    {booking.customer_phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white text-xs"
                        onClick={() => window.location.href = `tel:${booking.customer_phone}`}
                      >
                        <Phone className="w-3 h-3 mr-1" />
                        Call
                      </Button>
                    )}
                  </div>
                </div>

                <p className="text-[10px] text-gray-600 mt-2">
                  Created: {formatDate(booking.created_at)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};


// ============== Enquiries Tab Component ==============
const enquiryStatusStyles = {
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};
const enquiryStatusOptions = ['new', 'contacted', 'confirmed', 'closed'];

const EnquiriesTab = ({ enquiries, setEnquiries, loading, formatDate }) => {
  const [updatingId, setUpdatingId] = React.useState(null);

  const pence = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format((v || 0) / 100);

  const byStatus = (s) => enquiries.filter((e) => e.status === s).length;

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const res = await fetch(`${API_URL}/api/booking-enquiries/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status: newStatus } : e)));
    } catch (err) { console.error('Enquiry status update failed:', err); } finally {
      setUpdatingId(null);
    }
  };

  if (loading && enquiries.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#e2e8f0] animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Send className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{enquiries.length}</p>
                <p className="text-xs text-gray-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{byStatus('new')}</p>
                <p className="text-xs text-gray-400">New</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{byStatus('contacted')}</p>
                <p className="text-xs text-gray-400">Contacted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{byStatus('confirmed')}</p>
                <p className="text-xs text-gray-400">Confirmed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      {enquiries.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-10 text-center">
            <Send className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">No Booking Enquiries Yet</h3>
            <p className="text-gray-400">When customers submit booking requests, they will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {enquiries.map((enq) => (
            <Card
              key={enq.id}
              className="bg-slate-800/50 border-slate-700/50 hover:bg-slate-800/70 transition-colors"
              data-testid={`enquiry-card-${enq.id}`}
            >
              <CardContent className="p-5">
                {/* Header row */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div>
                      <p className="text-sm font-bold text-white">{enq.customer_name}</p>
                      <a href={`mailto:${enq.customer_email}`} className="text-xs text-blue-400 hover:underline">{enq.customer_email}</a>
                      {enq.customer_phone && (
                        <span className="text-xs text-gray-500 ml-2">{enq.customer_phone}</span>
                      )}
                    </div>
                  </div>

                  {/* Status dropdown */}
                  <div className="flex items-center gap-2">
                    <Badge className={`${enquiryStatusStyles[enq.status] || enquiryStatusStyles.new} border text-[10px] px-2`}>
                      {(enq.status || 'new').toUpperCase()}
                    </Badge>
                    <select
                      value={enq.status || 'new'}
                      onChange={(e) => updateStatus(enq.id, e.target.value)}
                      disabled={updatingId === enq.id}
                      className="bg-slate-700 text-gray-300 text-xs border border-slate-600 rounded px-2 py-1"
                      data-testid={`enquiry-status-${enq.id}`}
                    >
                      {enquiryStatusOptions.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm mb-3">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Package</p>
                    <p className="text-gray-200 font-medium">
                      {enq.package_name}
                      {enq.addon_package_name && <span className="text-gray-400 font-normal"> + {enq.addon_package_name}</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Event Date</p>
                    <p className="text-gray-200">{enq.event_date}{enq.num_days > 1 ? ` (${enq.num_days} days)` : ''}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Ex VAT</p>
                    <p className="text-gray-200">{pence(enq.charge_ex_vat_pence)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Inc VAT</p>
                    <p className="text-emerald-400 font-semibold">{pence(enq.charge_inc_vat_pence)}</p>
                  </div>
                </div>

                {/* Access details (if present) */}
                {(enq.load_in_time || enq.load_out_time || enq.load_in_access || enq.venue_access_notes) && (
                  <div className="bg-slate-900/40 rounded-lg px-3 py-2.5 mb-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 text-xs">
                    {enq.load_in_time && (
                      <div>
                        <span className="text-gray-500">Load-in: </span>
                        <span className="text-gray-300">{enq.load_in_time}</span>
                      </div>
                    )}
                    {enq.load_out_time && (
                      <div>
                        <span className="text-gray-500">Load-out: </span>
                        <span className="text-gray-300">{enq.load_out_time}</span>
                      </div>
                    )}
                    {enq.load_in_access && (
                      <div>
                        <span className="text-gray-500">Access: </span>
                        <span className="text-gray-300">{enq.load_in_access}</span>
                      </div>
                    )}
                    {enq.venue_access_notes && (
                      <div className="col-span-2 md:col-span-4">
                        <span className="text-gray-500">Venue notes: </span>
                        <span className="text-gray-300">{enq.venue_access_notes}</span>
                      </div>
                    )}
                  </div>
                )}

                {enq.additional_info && (
                  <p className="text-xs text-gray-400 mb-2">
                    <span className="text-gray-500">Additional: </span>{enq.additional_info}
                  </p>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-gray-600">Submitted: {formatDate(enq.created_at)}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white text-xs"
                      onClick={() => window.location.href = `mailto:${enq.customer_email}`}
                    >
                      <Mail className="w-3 h-3 mr-1" /> Email
                    </Button>
                    {enq.customer_phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white text-xs"
                        onClick={() => window.location.href = `tel:${enq.customer_phone}`}
                      >
                        <Phone className="w-3 h-3 mr-1" /> Call
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default Admin;
